// Core dependencies
import { connect } from 'react-redux';
import React from 'react';
import { yieldCallback } from '@zillow/yield-callback';

// Analytics

// Components
import Section from 'app/shared/modules/Section';

// Constants
import {
  APARTMENT_RENTALS,
  FOR_RENT_BY_OWNER,
  HOUSE_RENTALS,
  STATE_RENTALS,
  TOWNHOME_CONDOS_RENTALS,
} from './TopCitiesConstants';

// Icons
import IconArrowDown from 'images/icons/arrow-down.min.svg';
import IconArrowUp from 'images/icons/arrow-up.min.svg';

// Styles
import 'app/shared/modules/frontpage/TopCities.scss';
import {
  AccordionContent,
  AccordionHeader,
  AccordionIcon,
  AccordionTitle,
  CityItem,
  CityList,
  Column,
  ColumnContainer,
  DesktopStatesContainer,
  DesktopTitle,
  MobileStatesContainer,
  RentalTypesContainer,
  ShowMoreButton,
  ShowMoreCarat,
  StateColumn,
  StyledContainer,
  StyledLink,
  StyledTitle,
} from './TopCitiesStyles';

interface TopCitiesProps {
  isMobile: boolean;
  dispatch?: (action: any) => void; // Redux dispatch type
}

interface TopCitiesState {
  expandedColumns: {
    apartments: boolean;
    houses: boolean;
    byOwner: boolean;
    townhomes: boolean;
    states1: boolean;
    states2: boolean;
  };
  mobileAccordion: {
    apartments: boolean;
    houses: boolean;
    byOwner: boolean;
    townhomes: boolean;
    states: boolean;
  };
  allExpanded: boolean;
  rentalColumnExpansion: 'none' | 'firstRow' | 'secondRow' | 'all';
}

interface RentalItem {
  title: string;
  url: string;
}

class TopCities extends React.Component<TopCitiesProps, TopCitiesState> {
  state: TopCitiesState = {
    expandedColumns: {
      apartments: false,
      houses: false,
      byOwner: false,
      townhomes: false,
      states1: false,
      states2: false,
    },
    mobileAccordion: {
      apartments: false,
      houses: false,
      byOwner: false,
      townhomes: false,
      states: false,
    },
    allExpanded: false,
    rentalColumnExpansion: 'none',
  };

  yieldTrackAndTransitionTo = yieldCallback((path: string) => {
    window.router.transitionTo(path);
  });

  handleTopCityClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    this.yieldTrackAndTransitionTo(path);
  };

  toggleColumnExpansion = (column: keyof TopCitiesState['expandedColumns']) => {
    this.setState((prevState) => ({
      expandedColumns: {
        ...prevState.expandedColumns,
        [column]: !prevState.expandedColumns[column],
      },
    }));
  };

  toggleMobileAccordion = (type: keyof TopCitiesState['mobileAccordion']) => {
    this.setState((prevState) => ({
      mobileAccordion: {
        ...prevState.mobileAccordion,
        [type]: !prevState.mobileAccordion[type],
      },
    }));
  };

  toggleAllColumns = () => {
    this.setState((prevState) => {
      const newExpanded = !prevState.allExpanded;
      return {
        allExpanded: newExpanded,
        expandedColumns: {
          apartments: newExpanded,
          houses: newExpanded,
          byOwner: newExpanded,
          townhomes: newExpanded,
          states1: newExpanded,
          states2: newExpanded,
        },
      };
    });
  };

  isTabletViewport = () => {
    // Check if viewport is between 768px and 1019px
    return window.matchMedia('(min-width: 768px) and (max-width: 1019px)').matches;
  };

  toggleFirstRow = () => {
    this.setState((prevState) => {
      const isCurrentlyExpanded =
        prevState.rentalColumnExpansion === 'firstRow' || prevState.rentalColumnExpansion === 'all';
      const newExpansion = isCurrentlyExpanded ? 'none' : 'firstRow';

      // On lg and up, expand all columns
      if (!this.isTabletViewport()) {
        const shouldExpand = newExpansion !== 'none';
        return {
          rentalColumnExpansion: shouldExpand ? 'all' : 'none',
          expandedColumns: {
            ...prevState.expandedColumns,
            apartments: shouldExpand,
            houses: shouldExpand,
            byOwner: shouldExpand,
            townhomes: shouldExpand,
          },
        };
      }

      // On md (tablet), only expand first row columns
      return {
        rentalColumnExpansion: newExpansion,
        expandedColumns: {
          ...prevState.expandedColumns,
          apartments: newExpansion === 'firstRow',
          houses: newExpansion === 'firstRow',
        },
      };
    });
  };

  toggleSecondRow = () => {
    this.setState((prevState) => {
      const isCurrentlyExpanded =
        prevState.rentalColumnExpansion === 'secondRow' || prevState.rentalColumnExpansion === 'all';
      const newExpansion = isCurrentlyExpanded ? 'none' : 'secondRow';

      // On lg and up, expand all columns
      if (!this.isTabletViewport()) {
        const shouldExpand = newExpansion !== 'none';
        return {
          rentalColumnExpansion: shouldExpand ? 'all' : 'none',
          expandedColumns: {
            ...prevState.expandedColumns,
            apartments: shouldExpand,
            houses: shouldExpand,
            byOwner: shouldExpand,
            townhomes: shouldExpand,
          },
        };
      }

      // On md (tablet), only expand second row columns
      return {
        rentalColumnExpansion: newExpansion,
        expandedColumns: {
          ...prevState.expandedColumns,
          byOwner: newExpansion === 'secondRow',
          townhomes: newExpansion === 'secondRow',
        },
      };
    });
  };

  splitStatesIntoColumns(states: Array<RentalItem>, itemsPerColumn = 10): Array<Array<RentalItem>> {
    const columns: Array<Array<RentalItem>> = [];

    for (let i = 0; i < states.length; i += itemsPerColumn) {
      columns.push(states.slice(i, i + itemsPerColumn));
    }
    return columns;
  }

  renderColumnTitle(title: string, type: keyof TopCitiesState['mobileAccordion']) {
    return (
      <>
        <AccordionHeader onClick={() => this.toggleMobileAccordion(type)}>
          <AccordionIcon
            src={this.state.mobileAccordion[type] ? IconArrowUp : IconArrowDown}
            alt=""
            isOpen={this.state.mobileAccordion[type]}
          />
          <AccordionTitle>{title || 'Rentals by State'}</AccordionTitle>
        </AccordionHeader>
        <DesktopTitle>
          <StyledTitle size="md" htmlTag="h2">
            {title || 'Rentals by State'}
          </StyledTitle>
        </DesktopTitle>
      </>
    );
  }

  renderColumnList(type: keyof TopCitiesState['expandedColumns'], items: Array<RentalItem>) {
    const { expandedColumns, mobileAccordion, rentalColumnExpansion } = this.state;
    const { isMobile } = this.props;
    const isExpanded = expandedColumns[type];
    const isMobileExpanded =
      type in mobileAccordion ? mobileAccordion[type as keyof TopCitiesState['mobileAccordion']] : false;
    const shouldShowMoreButton = items.length > 10;

    // Determine which expanded state to use based on screen size and row
    let effectiveExpanded = isExpanded;
    let buttonExpanded = false;
    let onClickHandler = () => {
      // Default no-op handler
    };

    if (!isMobile && shouldShowMoreButton) {
      // Determine button state and handler based on which row this column is in
      if (type === 'apartments' || type === 'houses') {
        buttonExpanded = rentalColumnExpansion === 'firstRow' || rentalColumnExpansion === 'all';
        onClickHandler = this.toggleFirstRow;
        effectiveExpanded = expandedColumns[type];
      } else if (type === 'byOwner' || type === 'townhomes') {
        buttonExpanded = rentalColumnExpansion === 'secondRow' || rentalColumnExpansion === 'all';
        onClickHandler = this.toggleSecondRow;
        effectiveExpanded = expandedColumns[type];
      }
    }

    return (
      <AccordionContent isOpen={isMobileExpanded}>
        <CityList>
          {items.map((item, index) => (
            <CityItem key={item.title} isVisible={isMobile ? true : effectiveExpanded || index < 10}>
              <StyledLink
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => this.handleTopCityClick(e, item.url)}
                to={item.url}
              >
                {item.title}
              </StyledLink>
            </CityItem>
          ))}
        </CityList>
        {shouldShowMoreButton && !isMobile && (
          <ShowMoreButton onClick={onClickHandler}>
            {buttonExpanded ? 'Show Less' : 'Show More'}
            <ShowMoreCarat
              src={buttonExpanded ? IconArrowUp : IconArrowDown}
              height="10px"
              width="11px"
              alt=""
              aria-hidden="true"
            />
          </ShowMoreButton>
        )}
      </AccordionContent>
    );
  }

  renderListColumn(title: string, type: keyof TopCitiesState['expandedColumns'], items: Array<RentalItem>) {
    // Map expandedColumns type to mobileAccordion type (they're the same except for states1/states2)
    const mobileAccordionType = type as keyof TopCitiesState['mobileAccordion'];
    return (
      <Column>
        {this.renderColumnTitle(title, mobileAccordionType)}
        {this.renderColumnList(type, items)}
      </Column>
    );
  }

  renderStateColumns() {
    const { mobileAccordion } = this.state;

    // Always show all states on desktop, split into columns of 13 items each
    const itemsPerColumn = 13;
    const stateColumns = this.splitStatesIntoColumns(STATE_RENTALS, itemsPerColumn);

    return (
      <>
        {stateColumns.map((states, index) => (
          <StateColumn key={`states-column-${index}`}>
            {index === 0 ? this.renderColumnTitle('Rentals by State', 'states') : <DesktopTitle />}
            <AccordionContent isOpen={mobileAccordion.states}>
              <CityList>
                {states.map((item) => (
                  <CityItem key={item.title} isVisible={true}>
                    <StyledLink
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => this.handleTopCityClick(e, item.url)}
                      to={item.url}
                    >
                      {item.title}
                    </StyledLink>
                  </CityItem>
                ))}
              </CityList>
            </AccordionContent>
          </StateColumn>
        ))}
      </>
    );
  }

  render() {
    const { mobileAccordion } = this.state;
    const { isMobile } = this.props;

    return (
      <StyledContainer className="TopCities" size="lg">
        <Section>
          <ColumnContainer>
            <RentalTypesContainer data-testid="rental-types-by-city">
              {this.renderListColumn('Apartment Rentals', 'apartments', APARTMENT_RENTALS)}
              {this.renderListColumn('House Rentals', 'houses', HOUSE_RENTALS)}
              {this.renderListColumn('For Rent by Owner', 'byOwner', FOR_RENT_BY_OWNER)}
              {this.renderListColumn('Townhome & Condo Rentals', 'townhomes', TOWNHOME_CONDOS_RENTALS)}
            </RentalTypesContainer>

            {/* Mobile States Accordion */}
            <MobileStatesContainer>
              <AccordionHeader onClick={() => this.toggleMobileAccordion('states')}>
                <AccordionIcon
                  src={mobileAccordion.states ? IconArrowUp : IconArrowDown}
                  alt=""
                  isOpen={mobileAccordion.states}
                />
                <AccordionTitle>Rentals by State</AccordionTitle>
              </AccordionHeader>
              <AccordionContent isOpen={mobileAccordion.states}>
                <CityList>
                  {STATE_RENTALS.map((item) => (
                    <CityItem
                      key={item.title}
                      isVisible={isMobile ? true : mobileAccordion.states || STATE_RENTALS.indexOf(item) < 10}
                    >
                      <StyledLink
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => this.handleTopCityClick(e, item.url)}
                        to={item.url}
                      >
                        {item.title}
                      </StyledLink>
                    </CityItem>
                  ))}
                </CityList>
              </AccordionContent>
            </MobileStatesContainer>

            {/* Desktop States Columns */}
            <DesktopStatesContainer>{this.renderStateColumns()}</DesktopStatesContainer>
          </ColumnContainer>
        </Section>
      </StyledContainer>
    );
  }
}

const mapStateToProps = (state: { app: { device: { screenWidth: string } } }) => ({
  isMobile: state.app.device.screenWidth === 'sm' || state.app.device.screenWidth === 'xs',
});

export default connect(mapStateToProps)(TopCities);
